import { AuthLayout } from "@/components/layouts/AuthLayout";
import { useIdentity } from "@/components/contexts/IdentityContext/UseIdentity";
import { LoadingDots } from "@/components/ui/LoadingDots";
import { Logo } from "@/components/ui/Logo";
import { Button } from "@/components/ui/shadcn/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/shadcn/form";
import { FormInput } from "@/components/ui/shadcn/input";
import { Link } from "@/components/ui/shadcn/link";
import { Heading, Text } from "@/components/ui/Typography";
import { zodResolver } from "@hookform/resolvers/zod";
import { KeyRound, CheckCircle2 } from "lucide-react";
import { useCallback, useState } from "react";
import { useForm } from "react-hook-form";
import { useSearchParams } from "react-router";
import { z } from "zod";
import { animDelay } from "@/lib/utils";

const EmailFormSchema = z.object({
  email: z
    .string()
    .nonempty("Email is required")
    .email("Invalid email address"),
});

type EmailFormData = z.infer<typeof EmailFormSchema>;

function RequestPasswordResetPage() {
  const [success, setSuccess] = useState(false);
  const { requestPasswordReset } = useIdentity();
  const [searchParams] = useSearchParams();
  const isAgent = searchParams.get("isAgent") === "true";

  const form = useForm<EmailFormData>({
    resolver: zodResolver(EmailFormSchema),
    defaultValues: {
      email: "",
    },
  });

  const onSubmit = useCallback(
    async ({ email }: EmailFormData) => {
      form.clearErrors();

      try {
        await requestPasswordReset({ email });

        setSuccess(true);
      } catch {
        form.setError("root", {
          message: "Something went wrong. Please try again later.",
        });
      }
    },
    [form, requestPasswordReset],
  );

  if (success) {
    return (
      <AuthLayout>
        <div className="anim-scale-in flex flex-col items-center text-center">
          <Logo variant="A" className="mb-4 w-[144px] sm:w-[156px]" />
          <div
            className="anim-scale-in mt-6 flex size-16 items-center justify-center rounded-2xl bg-success/10"
            style={animDelay(200)}
          >
            <CheckCircle2 size={28} className="text-success" />
          </div>
          <div className="anim-slide-up" style={animDelay(300)}>
            <Heading
              variant="h1"
              weight="medium"
              className="mt-4 text-3xl sm:text-3xl"
            >
              Check your inbox
            </Heading>
          </div>
          <div className="anim-fade-in" style={animDelay(400)}>
            <Text variant="text3" className="mt-2 text-muted-foreground">
              If an account exists with this email address, you will receive an
              email with instructions to reset your password.
            </Text>
          </div>
          <div className="anim-slide-up mt-6 w-full" style={animDelay(500)}>
            <Link to={isAgent ? "/signin?isAgent=true" : "/signin"}>
              <Button variant="outline" className="w-full">
                Back to sign in
              </Button>
            </Link>
          </div>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <div className="flex flex-col items-center text-center">
        <header
          className="anim-slide-up flex flex-col items-center gap-4"
          style={animDelay(50)}
        >
          <Logo variant="A" className="mb-4 w-[144px] sm:w-[156px]" />
          <div
            className="anim-scale-in flex size-16 items-center justify-center rounded-2xl bg-primary/10"
            style={animDelay(150)}
          >
            <KeyRound size={28} className="text-primary" />
          </div>
          <Heading
            variant="h1"
            weight="medium"
            className="text-3xl sm:text-3xl"
          >
            Forgot Password
          </Heading>
          <Text variant="text3" className="max-w-xs text-muted-foreground">
            Enter your email and we&apos;ll send you a link to reset your
            password.
          </Text>
        </header>

        <div className="anim-slide-up mt-6 w-full" style={animDelay(200)}>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="w-full">
              <div className="mx-auto flex w-full flex-col items-center text-left">
                <div className="flex w-full flex-col gap-5">
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <FormInput
                            placeholder="Enter your email"
                            autoComplete="email"
                            disabled={form.formState.isSubmitting}
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <Button
                  type="submit"
                  className="mt-6 w-full"
                  disabled={form.formState.isSubmitting}
                >
                  {form.formState.isSubmitting ? (
                    <LoadingDots color="current" />
                  ) : (
                    "Send reset link"
                  )}
                </Button>

                {form.formState.errors.root && (
                  <div className="anim-slide-down">
                    <Text variant="text3" className="mt-3 text-destructive">
                      {form.formState.errors.root.message}
                    </Text>
                  </div>
                )}
              </div>
            </form>
          </Form>
        </div>

        <div className="anim-fade-in mt-5" style={animDelay(300)}>
          <Text variant="text3">
            Remember your password?{" "}
            <Link to={isAgent ? "/signin?isAgent=true" : "/signin"}>
              Sign in
            </Link>
          </Text>
        </div>
      </div>
    </AuthLayout>
  );
}

export default RequestPasswordResetPage;
